import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/api";
import { copy } from "@/i18n";
import { projects as seedProjects } from "@/data/projects";

const flattenGallery = (g) =>
  g.flatMap((b) =>
    b.type === "full" ? [b.src] : [b.left, b.right]
  );

const defaults = {
  projects: seedProjects.map((p, i) => ({
    ...p,
    order: i,
    gallery: flattenGallery(p.gallery),
  })),

  team: [
    {
      id: "muhammed-oy",
      order: 0,
      name: "Muhammed Oy",
      title: { tr: "", en: "" },
      bio: { tr: "", en: "" },
      portrait: "",
      active: true,
    },
    {
      id: "gonul-celik-oy",
      order: 1,
      name: "Gönül Çelik Oy",
      title: { tr: "", en: "" },
      bio: { tr: "", en: "" },
      portrait: "",
      active: true,
    },
  ],

  services: copy.tr.services.items.map((s, i) => ({
    id: `service-${i}`,
    order: i,
    title: {
      tr: s.title,
      en: copy.en.services.items[i].title,
    },
    text: {
      tr: s.text,
      en: copy.en.services.items[i].text,
    },
  })),

  site: {
    studio: {
      statement: {
        tr: copy.tr.studio.statement,
        en: copy.en.studio.statement,
      },
      about: {
        tr: copy.tr.studio.about,
        en: copy.en.studio.about,
      },
      approach: {
        tr: copy.tr.studio.approach,
        en: copy.en.studio.approach,
      },
    },

    manifesto: {
      tr: copy.tr.manifesto.chapters,
      en: copy.en.manifesto.chapters,
    },

    cta: {
      tr: copy.tr.cta.title,
      en: copy.en.cta.title,
    },

    contact: {
      email: "hello@solidor.studio",
      instagram: "https://instagram.com",
      location: {
        tr: "İstanbul, Türkiye",
        en: "Istanbul, Turkey",
      },
      lines: {
        tr: copy.tr.contact.lines,
        en: copy.en.contact.lines,
      },
    },

    seo: {
      home: {
        title: "SOLIDOR — Architecture & Design",
        description:
          "SOLIDOR is an architecture and design studio based in Istanbul, working across architecture, interiors and spatial design.",
      },
      studio: {
        title: "Studio — SOLIDOR",
        description:
          "SOLIDOR is an Istanbul-based architecture and interior design studio.",
      },
      services: {
        title: "Services — SOLIDOR",
        description:
          "Architecture, interior architecture, design, visualization and project development.",
      },
      contact: {
        title: "Contact — SOLIDOR",
        description:
          "Let's create something together. Contact SOLIDOR in Istanbul.",
      },
      og_image: "",
    },
  },
};

const Ctx = createContext(defaults);

export const ContentProvider = ({ children }) => {
  const [cms, setCms] = useState(defaults);

  useEffect(() => {
    axios
      .get(`${API}/content`)
      .then((r) => {
        const d = r.data || {};
        const remoteSite = d.site || {};

        setCms({
          projects:
            d.projects?.length
              ? d.projects
              : defaults.projects,

          team:
            d.team?.length
              ? d.team
              : defaults.team,

          services:
            d.services?.length
              ? d.services
              : defaults.services,

          site: {
            ...defaults.site,
            ...remoteSite,

            studio: {
              ...defaults.site.studio,
              ...(remoteSite.studio || {}),
            },

            manifesto: {
              ...defaults.site.manifesto,
              ...(remoteSite.manifesto || {}),
            },

            cta: {
              ...defaults.site.cta,
              ...(remoteSite.cta || {}),
            },

            contact: {
              ...defaults.site.contact,
              ...(remoteSite.contact || {}),

              location: {
                ...defaults.site.contact.location,
                ...(remoteSite.contact?.location || {}),
              },

              lines: {
                ...defaults.site.contact.lines,
                ...(remoteSite.contact?.lines || {}),
              },
            },

            seo: {
              ...defaults.site.seo,
              ...(remoteSite.seo || {}),

              home: {
                ...defaults.site.seo.home,
                ...(remoteSite.seo?.home || {}),
              },

              studio: {
                ...defaults.site.seo.studio,
                ...(remoteSite.seo?.studio || {}),
              },

              services: {
                ...defaults.site.seo.services,
                ...(remoteSite.seo?.services || {}),
              },

              contact: {
                ...defaults.site.seo.contact,
                ...(remoteSite.seo?.contact || {}),
              },
            },
          },
        });
      })
      .catch(() => {
        // Backend ulaşılamazsa varsayılan içerikle site çalışmaya devam eder.
      });
  }, []);

  return (
    <Ctx.Provider value={cms}>
      {children}
    </Ctx.Provider>
  );
};

export const useContent = () => useContext(Ctx);
